import { FC, useContext, useState } from 'react';
import { NextRouter, useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import triggerTracking from '@blueprint/tracking';

import {
    isTravelTimeSearchSelector,
    filtersValuesSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '@zoopla/redux-reducer-filters';

import { selectCurrentSortOrder } from '@zoopla/redux-reducer-sort-order';
import {
    BedroomsWrapper,
    SearchInitationWrapper,
    PriceRangeMultiColumnDropdownWrapper,
    PropertyTypeWrapper,
    SearchRadiusWrapper,
    StyledButtonWrapper,
    StyledButton,
    TravelTimeWrapper,
    StyledDivider,
    StyledDividerInner,
    LocationWrapper,
    HousePricesLocationWrapper
} from './SearchInitiation.styles';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectRadius } from './SelectRadius';
import { SelectBeds } from './SelectBeds';
import { SelectPropertyType } from './SelectPropertyType';
import { SelectMaxTravelTimeAndTransportMode } from './SelectMaxTravelTimeAndTransportMode';
import { SearchContext, SearchSource } from '@zoopla/listing-search-context';
import { SearchPageType } from '@zoopla/listing-search-context';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';
import { ThemeProvider } from '@blueprint/ui';
import { searchHousePrices, searchProperties } from 'search/helpers';
import { zooplaTheme } from '@blueprint/theme';
import useSearchLocation from 'search/hooks/useSearchLocation';

const SEARCH_BUTTON_TEXT = 'Search';

type SearchInitiationProps = {
    searchType: Tab;
    showRadius?: boolean;
    eventCategory: 'homepage' | 'search results' | 'new_homes';
    searchSource: SearchSource;
    excludePageSize?: boolean;
    onSearchClick?: (args: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filters: any;
        router: NextRouter;
    }) => void;
    hidePropertyType?: boolean;
};

export const SearchInitiation: FC<SearchInitiationProps> = ({
    searchType,
    showRadius = true,
    eventCategory,
    searchSource,
    excludePageSize = false,
    onSearchClick,
    hidePropertyType = false
}) => {
    const router = useRouter();

    const { state, dispatch } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);
    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const { selectedValue: locationTextValue } =
        useSelector(locationTextSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);

    const { userAlertId } = state;
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isHousePricesSearch = searchType === 'house-prices';

    const value = locationTextValue || locationValue;
    const locationValueText = useSearchLocation();

    const handleSearchProperties = () => {
        const eventLabel = !!value
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;

        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        if (onSearchClick) {
            onSearchClick({
                filters,
                router
            });
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            searchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
    };

    const renderRadiusAndTravelTime = () => {
        if (!isTravelTimeSearch) {
            if (showRadius) {
                return (
                    <>
                        <StyledDivider>
                            <StyledDividerInner />
                        </StyledDivider>
                        <SearchRadiusWrapper>
                            <SelectRadius />
                        </SearchRadiusWrapper>
                    </>
                );
            }

            return null;
        }

        return (
            <ThemeProvider theme={zooplaTheme}>
                <TravelTimeWrapper>
                    <SelectMaxTravelTimeAndTransportMode />
                </TravelTimeWrapper>
            </ThemeProvider>
        );
    };

    if (isHousePricesSearch) {
        return (
            <ThemeProvider theme={zooplaTheme}>
                <SearchInitationWrapper>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasExtendedHeight
                            hasDarkPurpleBorder
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValueText}
                            onlyDisplayRemoveIconWhenActive={false}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapper>
                        <StyledButton
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="house-prices-search-button"
                            disabled={!value}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButton>
                    </StyledButtonWrapper>
                </SearchInitationWrapper>
            </ThemeProvider>
        );
    }

    const showError = hasSubmittedLocationSearch && !value;

    return (
        <ThemeProvider theme={zooplaTheme}>
            <SearchInitationWrapper>
                <LocationWrapper>
                    <LocationSearchInput
                        id="header-location"
                        onLocationInputKeyDown={
                            onSearchClick
                                ? () =>
                                      onSearchClick({
                                          filters,
                                          router
                                      })
                                : handleSearchProperties
                        }
                        hasExtendedHeight
                        hasDarkPurpleBorder
                        hasBorderRight={false}
                        value={locationValueText}
                        showError={showError}
                        onlyDisplayRemoveIconWhenActive={false}
                    />
                </LocationWrapper>
                {renderRadiusAndTravelTime()}

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <BedroomsWrapper>
                    <SelectBeds />
                </BedroomsWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PriceRangeMultiColumnDropdownWrapper>
                    {isForSaleSearch ? (
                        <SelectPriceForSale />
                    ) : (
                        <SelectPriceToRent />
                    )}
                </PriceRangeMultiColumnDropdownWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                {hidePropertyType ? null : (
                    <PropertyTypeWrapper>
                        <SelectPropertyType
                            excludeFarms={searchSource === 'new-homes-dev'}
                        />
                    </PropertyTypeWrapper>
                )}
                <StyledButtonWrapper>
                    <StyledButton
                        type="button"
                        onClick={handleSearchProperties}
                        data-testid="search-button"
                    >
                        {SEARCH_BUTTON_TEXT}
                    </StyledButton>
                </StyledButtonWrapper>
            </SearchInitationWrapper>
        </ThemeProvider>
    );
};
